import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, GoldBtn, GhostBtn, scrollToId, BrushHighlight } from "@/components/sections/shared";
import FoldText from "@/components/FoldText/FoldText";

const footerLinks = [
  { label: "Ask Jack", action: "ask" },
  { label: "Rip-Off Detector", href: "/rip-off-detector" },
  { label: "Report My Claim", target: "community" },
  { label: "About Inssux", target: "meet-jack" },
  { label: "Resources", target: "resources" },
  { label: "FAQ", target: "faq" },
  { label: "Privacy Policy" },
  { label: "Terms" },
  { label: "Disclaimer" },
];

export const FinalCTA = () => {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-30%", "30%"]);

  return (
  <section ref={ref} id="final-cta" data-testid="final-cta-section" className="relative bg-black py-28 md:py-40 overflow-hidden">
    <motion.img
      src="/jack-file.jpg"
      alt=""
      style={{ y }}
      className="absolute left-0 w-full h-[160%] -top-[30%] object-cover opacity-60"
    />
    <div
      className="absolute inset-0"
      style={{ background: "linear-gradient(to right, rgba(5,5,8,0.92) 30%, rgba(5,5,8,0.55) 65%, rgba(5,5,8,0.35))" }}
    />
    <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black to-transparent" />
    <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black to-transparent" />
    <div className="relative max-w-[1080px] mx-auto px-6 lg:px-14 text-left">
      <Reveal>
        <h2 className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-white max-w-3xl">
          <FoldText
            text="You do not have to"
            splitBy="word"
            trigger="scroll"
            fontSize="inherit"
            fontWeight="inherit"
            color="inherit"
          /> <br className="hidden sm:block" />
          <FoldText
            text="decode this alone."
            splitBy="word"
            trigger="scroll"
            fontSize="inherit"
            fontWeight="inherit"
            color="#9D4EDD"
          />
        </h2>
        <p className="mt-6 max-w-2xl text-lg text-[#A1A1AA] font-light leading-relaxed">
          If your claim was denied, delayed, confusing, or lower than expected, Jack can help you
          understand what may be happening and what to do next.
        </p>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="mt-10 flex flex-wrap items-center gap-4">
          <GoldBtn testId="final-primary-cta" onClick={() => navigate("/rip-off-detector")}>
            TAKE THE <BrushHighlight>FREE</BrushHighlight> RIP-OFF DETECTOR
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
          </GoldBtn>
          <GhostBtn testId="final-secondary-cta" onClick={() => window.AIWidget?.ask()}>
            ASK JACK
          </GhostBtn>
        </div>
        <p className="mt-8 font-mono-file text-[10px] tracking-[0.2em] text-[#71717A] uppercase">
          Educational guidance only. Not legal advice. Not an insurance company. Not a law firm.
          Individual claim outcomes vary.
        </p>
      </Reveal>
    </div>
  </section>
  );
};

export const Footer = () => {
  const navigate = useNavigate();

  return (
  <footer data-testid="site-footer" className="bg-black border-t border-white/10 py-14">
    <div className="max-w-[1280px] mx-auto px-6 lg:px-14">
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-10">
        <div className="max-w-md">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 bg-[#8A2BE2] rotate-45" />
            <span className="font-display text-xl tracking-[0.3em] text-white">INSSUX</span>
          </div>
          <p className="mt-4 text-sm text-[#A1A1AA] font-light leading-relaxed">
            Inssux helps policyholders understand insurance claims, spot potential red flags, and
            make informed next-step decisions with help from the Ask Jack, the AI powered assistance engine
          </p>
        </div>
        <nav className="grid grid-cols-2 sm:grid-cols-3 gap-x-12 gap-y-3">
          {footerLinks.map((l) => (
            <button
              key={l.label}
              data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={
                l.action === "ask"
                  ? () => window.AIWidget?.ask()
                  : l.href
                  ? () => navigate(l.href)
                  : l.target
                  ? () => scrollToId(l.target)
                  : undefined
              }
              className="text-left text-sm text-[#71717A] font-light hover:text-[#FFD166] transition-colors duration-200"
            >
              {l.label}
            </button>
          ))}
        </nav>
      </div>
      <div className="mt-12 pt-8 border-t border-white/[0.06]">
        <p className="text-[11px] text-[#71717A] font-light leading-relaxed max-w-4xl">
          Inssux provides educational information and AI-powered claim guidance. Inssux is not a
          law firm, insurance company, or licensed public adjusting firm. Jack D. Hapsburg does not
          provide legal advice, public adjusting services, or guaranteed claim outcomes. Always
          consult a qualified professional when legal, insurance, or adjusting advice is required.
        </p>
        <p className="mt-4 font-mono-file text-[10px] tracking-[0.25em] text-[#71717A]">
          © 2026 INSSUX
        </p>
      </div>
    </div>
  </footer>
  );
};
